import React, { useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const GUESTHOUSE_API_BASE = `${API_ROOT}/api/guesthouse`;

const nw = { whiteSpace: "nowrap" };

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function displayRoomType(rt) {
  if (rt === "R") return "Room";
  if (rt === "S") return "Suite";
  if (rt === "H") return "Seminar Hall";
  return rt || "";
}

function displayVisitType(vt) {
  if (vt === "P") return "Personal";
  if (vt === "O") return "Official";
  return vt || "";
}

function statusColor(status) {
  if (status === "Booked") return "green";
  if (status === "Pending") return "saddlebrown";
  if (status === "Cancelled") return "red";
  return undefined;
}

export default function IndividualLedger() {
  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);

  const [mobile, setMobile] = useState("");
  const [referenceUsers, setReferenceUsers] = useState([]);
  const [referenceID, setReferenceID] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (![10, 25].includes(moduleId)) {
    return (
      <section className="container-fluid">
        <div className="alert alert-danger mt-3">Unauthorized access Or Session Timed out.</div>
      </section>
    );
  }

  const fetchUsers = async (mobileOverride) => {
    const mob = mobileOverride !== undefined ? mobileOverride : mobile;
    if (!/^\d{10}$/.test(mob)) {
      setError("Enter a valid 10-digit registered reference mobile number.");
      setReferenceUsers([]);
      setReferenceID("");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const q = new URLSearchParams({ mobile: mob, moduleId: String(moduleId) });
      const res = await fetch(`${GUESTHOUSE_API_BASE}/users-by-mobile?${q}`);
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to fetch users");

      const list = Array.isArray(data) ? data : [];
      setReferenceUsers(list);
      setReferenceID(list[0]?.ID || "");

      if (!list.length) setError("No registered user found for this mobile number.");
    } catch (err) {
      setReferenceUsers([]);
      setReferenceID("");
      setError(err.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async () => {
    if (!referenceID) {
      setError("Please select a Reference ID.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setRows([]);

      const q = new URLSearchParams({ referenceID, moduleId: String(moduleId) });
      if (selectedDate) q.set("date", selectedDate);

      const res = await fetch(`${GUESTHOUSE_API_BASE}/individual-ledger?${q}`);
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Failed to load ledger");

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  const openPrintForm = (bookingId) => {
    const q = new URLSearchParams({ moduleId: String(moduleId) });
    window.open(
      `${GUESTHOUSE_API_BASE}/booking/print/${encodeURIComponent(bookingId)}?${q.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openEdit = (bookingId) => {
    window.open(
      `/guesthouse/admin-edit/${encodeURIComponent(bookingId)}?moduleId=${moduleId}`,
      "editBooking",
      "width=1000,height=600,scrollbars=yes,resizable=yes"
    );
  };

  const openInvoice = (bookingId) => {
    window.open(
      `${GUESTHOUSE_API_BASE}/booking/invoice-pdf/${encodeURIComponent(bookingId)}?moduleId=${moduleId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const viewAttachment = (supportDoc) => {
    if (!supportDoc) { window.alert("No attachment found."); return; }
    const normalized = String(supportDoc).trim().replace(/\\/g, "/");
    const fileName = normalized.split("/").pop();
    if (!fileName) { window.alert("Attachment path could not be resolved."); return; }
    window.open(
      `${GUESTHOUSE_API_BASE}/ghbooking_request/${encodeURIComponent(fileName)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section className="container-fluid">
      <h3 className="ctitle">Individual Ledger</h3>

      <div style={{ textAlign: "left", marginTop: "10px", marginBottom: "16px" }}>
        <a
          href="/payment-details"
          className="btn btn-primary"
          style={{ display: "inline-block", padding: "10px 20px", textDecoration: "none" }}
        >
          Payment Details
        </a>
      </div>

      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label">Registered Ref. Mobile</label>
          <input
            className="form-control"
            type="text"
            value={mobile}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 10);
              setMobile(val);
              if (val.length === 10) fetchUsers(val);
            }}
            onBlur={() => fetchUsers()}
            placeholder="Mobile...*"
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Registered Reference User</label>
          <select
            className="form-select"
            value={referenceID}
            onChange={(e) => setReferenceID(e.target.value)}
          >
            <option value="">-- Select Name --</option>
            {referenceUsers.map((item) => (
              <option key={item.ID} value={item.ID}>
                {`${item.Name} (${item.ID})`}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">From Date (optional)</label>
          <input
            className="form-control"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={fetchLedger}
            disabled={loading || !referenceID}
          >
            View
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading && <div className="text-muted mb-2">Loading...</div>}

      {!loading && rows.length > 0 && (
        <div className="table-responsive">
          <table
            className="table table-bordered align-middle"
            style={{ borderCollapse: "collapse" }}
          >
            <thead style={{ "--bs-table-bg": "#008080", color: "white" }}>
              <tr>
                <th style={nw}>SNo</th>
                <th style={nw}>BookDate</th>
                <th style={nw}>Visitor Name</th>
                <th style={nw}>Mobile</th>
                <th style={nw}>Visit</th>
                <th style={nw}>GuestHouse</th>
                <th style={nw}>Type</th>
                <th style={nw}>CheckIn</th>
                <th style={nw}>CheckOut</th>
                <th style={nw}>Status</th>
                <th style={nw}>Ref.ID</th>
                <th style={nw}>Form</th>
                <th style={nw}>Attachment</th>
                <th style={nw}>ID Occupant</th>
                <th style={nw}>Invoice</th>
                <th style={nw}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row.BookingID}
                  style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f8f9fa" }}
                >
                  <td style={nw}>{idx + 1}</td>
                  <td style={nw}>{formatDate(row.BookingDate)}</td>
                  <td style={nw}>{row.VisitorName || ""}</td>
                  <td style={nw}>{row.Mobile || ""}</td>
                  <td style={nw}>{displayVisitType(row.TypeOfVisit)}</td>
                  <td style={nw}>{row.GuestHouseAbb || row.GuestHouse || ""}</td>
                  <td style={nw}>{displayRoomType(row.RoomType)}</td>
                  <td style={nw}>{formatDate(row.CheckIn)}</td>
                  <td style={nw}>{formatDate(row.CheckOut)}</td>
                  <td style={nw}>
                    <span style={{ color: statusColor(row.Status) }}>
                      {row.Status || ""}
                    </span>
                  </td>
                  <td style={nw}>{row.BookingID}</td>
                  <td style={nw}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openPrintForm(row.BookingID); }}>Form</a>
                  </td>
                  <td style={nw}>
                    {row.SupportDoc ? (
                      /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                      <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); viewAttachment(row.SupportDoc); }}>Attachment</a>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td style={nw}>
                    {row.ICardOccupant ? (
                      /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                      <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); viewAttachment(row.ICardOccupant); }}>ID Occupant</a>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td style={nw}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openInvoice(row.BookingID); }}>Invoice</a>
                  </td>
                  <td style={nw}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" style={{ color: "#000" }} onClick={e => { e.preventDefault(); openEdit(row.BookingID); }}>Edit</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length === 0 && referenceID && !error && (
        <div className="text-muted">No records found for the selected reference user.</div>
      )}
    </section>
  );
}
